import { goTo } from '@adornis/router/client/open-href.js';

import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { acss } from '@adornis/chemistry/directives/acss.js';
import { css } from '@adornis/chemistry/directives/css.js';
import '@adornis/chemistry/elements/components/x-flex.js';
import '@adornis/chemistry/elements/components/x-overlay.js';
import '@adornis/file-utils/client/x-image.js';
import { NO_CHANGE } from '@adornis/router/no-change.js';
import '@adornis/user-forms/x-login-input.js';
import '@adornis/user-forms/x-login.js';
import { XLogin } from '@adornis/user-forms/x-login.js';
import { BaseError } from '@adornis/validation/errors/BaseError.js';
import { getLogo } from '@ils/examType/logos.js';
import type { ExamType } from '@ils/static/enums.js';
import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { ILSUser } from '../../db/ils-user.js';
import '../pages/ils-reset-password.js';
import { ILSResetPasswordDialog } from './ils-reset-password-dialog.js';

@customElement('ils-login')
export class ILSLogin extends ChemistryLitElement {
  override styles() {
    return [...super.styles(), { ':host': { display: 'flex', height: '100%', width: '100%' } }];
  }
  @property({ type: Boolean, attribute: 'show-no-account' }) showNoAccount?: boolean;
  @property({ type: Boolean, attribute: 'no-reroute-on-login', reflect: true }) noRerouteOnLogin?: boolean;
  @property({ type: String, attribute: 'exam-type' }) examType: ExamType | 'platform' = 'platform';

  override render() {
    return html`
      <x-flex
        horizontal
        center
        crossaxis-center
        ${acss({
          height: '100%',
          width: '100%',
          '& [disabled]': {
            filter: 'grayscale(1)',
            pointerEvents: 'none',
          },
          overflowY: 'auto',
        })}
        space="md"
      >
        <ils-elements-login
          header-src=${getLogo('big', this.examType)}
          .userClass=${ILSUser}
          @login=${(e: CustomEvent) =>
            !this.noRerouteOnLogin
              ? goTo({ path: '/start', searchParams: NO_CHANGE })
              : this.dispatchEvent(new CustomEvent('login', { detail: e.detail }))}
        >
          <x-button icon="lock" mode="text" @click=${() => ILSResetPasswordDialog.showPopup({ modal: true })}>
            Passwort vergessen?
          </x-button>
          ${this.showNoAccount
            ? html`
                <x-button
                  icon="person_add"
                  mode="text"
                  @click=${() => this.dispatchEvent(new CustomEvent('no-account'))}
                >
                  Ich habe noch keinen Account
                </x-button>
              `
            : nothing}
        </ils-elements-login>
      </x-flex>
    `;
  }
}

/**
 * x-login with overlay breaks in embedded webcomponent context
 */
@customElement('ils-elements-login')
export class ILSElementsLogin extends XLogin {
  @state() private _needsOtp = false;
  private _otpCode = '';

  override render() {
    return html`
      <x-flex center crossaxis-center ${css({ height: '100%' })}>
        <x-flex space="md" ${css({ maxWidth: this.sizes.maxReadableWidth, minWidth: '300px' })}>
          ${this.headerSrc
            ? html`
                <x-flex crossaxis-center>
                  <img src=${this.headerSrc} alt="Logo" ${css({ maxWidth: '200px' })} />
                </x-flex>
              `
            : nothing}
          <slot name="header"></slot>
          <slot name="login-fields"></slot>
          <x-flex space="md">
            <x-flex space="sm" ${css({ display: this._needsOtp ? 'none' : 'flex' })}>
              <x-login-input
                ${css({ width: '100%' })}
                ?disabled=${this._loading}
                name="username"
                id="username"
                type="text"
                placeholder-mode="inline-floating"
                autocomplete
                @submit=${() => this._login()}
                @value-changed=${e => (this._email = e.detail.value || '')}
                placeholder=${this.placeholderUsername}
              ></x-login-input>
              <x-login-input
                ${css({ width: '100%' })}
                .disabled=${this._loading}
                name="password"
                id="password"
                type="password"
                @value-changed=${e => (this._password = e.detail.value || '')}
                @submit=${() => this._login()}
                placeholder=${this.placeholderPassword}
              ></x-login-input>
            </x-flex>
            <x-flex space="sm" ${css({ display: this._needsOtp ? 'flex' : 'none' })}>
              <x-text> Bitte Verifizierungscode angeben </x-text>
              <x-login-input
                ${css({ width: '100%' })}
                ?disabled=${this._loading}
                name="otp"
                id="otp"
                type="text"
                placeholder-mode="inline-floating"
                autocomplete
                @submit=${() => this._login()}
                @value-changed=${e => (this._otpCode = e.detail.value || '')}
                placeholder="OTP"
              ></x-login-input>
              <x-text tone="subtle" ${css({ maxWidth: '300px', fontSize: '0.8em' })}>
                Der Verifizierungscode wird von Ihrer Zwei-Faktor Authentifizierungsanwendung generiert. Falls Sie
                keinen Zugriff auf Ihre Authentifizierungsanwendung haben, kontaktieren Sie bitte den Support.
              </x-text>
            </x-flex>
          </x-flex>
          ${this._errorMessage ? html` <x-text align="center" tone="error"> ${this._errorMessage} </x-text> ` : nothing}
          <x-button ?disabled=${this._loading} @click=${() => this._login()}> Login </x-button>
          <slot></slot>
        </x-flex>
      </x-flex>
    `;
  }

  override async login({ username, password }: { username?: string; password?: string }) {
    if (!password?.length || !username?.length) throw new Error('Inputs are empty');
    const passwordWithOtp = JSON.stringify({ password, otp: this._otpCode });
    try {
      await super.login({ username, password: passwordWithOtp });
    } catch (err: any) {
      if (err instanceof BaseError && err.extensions.code === 405) {
        this._needsOtp = true;
        console.log('login needs otp!');
        return;
      }
      this._needsOtp = false;
      throw err;
    }
  }
}
